import * as React from 'react';
import { createContext } from 'react';

import { useDispatch } from 'react-redux';

import { EmbedVideoEventAdapter } from '~analytics/adapters/EmbedVideoEventAdapter';
import { EmbedAudioVideoPaywall } from '~teacher/components/EmbedAudioVideoPaywall';
import { EmbedVideoDialog } from '~teacher/components/QuizQuestion/EmbedVideoDialog/EmbedVideoDialog';

import { updateQuestionVideoData } from '~teacher/slices/quiz/quizSlice';
import { proCheck } from '~teacher/util/proCheck';

interface VideoData {
  url: string;
  start?: number;
  end?: number;
  duration: number;
}

type EmbedVideoDialogState = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  onSubmit: (data: VideoData) => void;
};

const EmbedVideoDialogContext = createContext<EmbedVideoDialogState>({
  isOpen: false,
  open: () => {},
  close: () => {},
  onSubmit: () => {},
});

export function EmbedVideoDialogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isEmbedVideoDialogOpen, setIsEmbedVideoDialogOpen] =
    React.useState(false);
  const [isEmbedVideoPaywallOpen, setIsEmbedVideoPaywallOpen] =
    React.useState(false);
  const dispatchRedux = useDispatch();

  const openEmbedVideoDialog = () => {
    if (proCheck.canEmbedMedia()) {
      setIsEmbedVideoDialogOpen(true);
    } else {
      setIsEmbedVideoPaywallOpen(true);
    }
  };

  return (
    <EmbedVideoDialogContext.Provider
      value={{
        isOpen: isEmbedVideoDialogOpen,
        open: openEmbedVideoDialog,
        close: () => setIsEmbedVideoDialogOpen(false),
        onSubmit: ({ url, start, end, duration }) => {
          const provider = getVideoProvider(url);
          setIsEmbedVideoDialogOpen(false);
          dispatchRedux(
            updateQuestionVideoData({
              url,
              start,
              end,
              duration,
              provider,
            })
          );

          // Send Analytics
          new EmbedVideoEventAdapter({
            provider,
            start,
            end,
            duration,
          }).sendAnalytics();
        },
      }}
    >
      {children}
      <EmbedVideoDialog />
      <EmbedAudioVideoPaywall
        open={isEmbedVideoPaywallOpen}
        onClose={() => setIsEmbedVideoPaywallOpen(false)}
      />
    </EmbedVideoDialogContext.Provider>
  );
}

export function useEmbedVideoModal() {
  const context = React.useContext(EmbedVideoDialogContext);
  if (context === undefined) {
    throw new Error(
      'useEmbedVideoModal must be used within a EmbedVideoDialogContext'
    );
  }
  return context;
}

type VideoProvider = 'youtube' | 'vimeo';

function getVideoProvider(url: string): VideoProvider {
  const lowerCaseUrl = url.toLowerCase();
  if (
    lowerCaseUrl.includes('youtube.com') ||
    lowerCaseUrl.includes('youtu.be') ||
    lowerCaseUrl.includes('youtube-nocookie.com') ||
    lowerCaseUrl.includes('youtube-education.com')
  ) {
    return 'youtube';
  } else if (
    lowerCaseUrl.includes('vimeo.com') ||
    lowerCaseUrl.includes('player.vimeo.com')
  ) {
    return 'vimeo';
  }
  return null;
}
